import { canonicalize, hashCanonicalB64, openContent, type WebCryptoKey } from '@viko/crypto'
import {
  ChangelogReport,
  DeviceAlertReport,
  DigestReport,
  REPORT_PAGE_BYTES,
  REPORT_PLAINTEXT_BYTES,
  type ReportHead,
  type ReportKind,
  ReportListPage,
  ReportListRequest,
  ReportPlaintext,
  ReportReadPage,
  ReportReadRequest,
  ReportStream,
  reportBytes,
  reportKind,
  reportSourceId,
} from '@viko/protocol'
import type { Api } from './api.js'
import { contentRequestError, isUnprovisioned } from './content-errors.js'
import { idb } from './idb.js'
import { projectReport, type ReportProjection } from './report-projection.js'
import { type RequestMeter, requestMeter } from './request-meter.js'
import { shortCache } from './short-cache.js'
import type { Machine } from './types.js'

export interface ReportTransport {
  list(input: typeof ReportListRequest._output): Promise<unknown>
  read(input: typeof ReportReadRequest._output): Promise<unknown>
}
const sameStream = (a: ReportStream, b: ReportStream) =>
  a.machineId === b.machineId && a.kind === b.kind && a.id === b.id
interface Checkpoint {
  epoch: number
  head: ReportHead
  source: string
  projection: ReportProjection
}
/** Durable checkpoints and projections are endpoint-only and account scoped. */
export class BrowserReports {
  private locked = false
  /**
   * burst-guard: verified checkpoints of this session, in memory. A listed head equal to the
   * memorized one returns its projection with no IndexedDB read and no network.
   */
  private readonly memo = new Map<string, Checkpoint>()
  /** One authenticated read per (stream, advertised head) at a time. */
  private readonly reading = new Map<string, Promise<ReportProjection>>()
  constructor(
    private readonly options: {
      accountId: string
      keyForEpoch(epoch: number): Promise<WebCryptoKey>
      transport: ReportTransport
      /** Endpoint check that neither the relay nor this browser's pin has a directory. */
      unprovisioned?(): Promise<boolean>
      /** Keep the in-memory checkpoint cache (default true; false = legacy). */
      memo?: boolean
    },
  ) {}
  lock(): void {
    this.locked = true
    this.memo.clear()
    this.reading.clear()
  }
  private active() {
    if (this.locked) throw new Error('reports locked')
  }
  private key(s: ReportStream) {
    return `report-checkpoint/v1/${JSON.stringify([this.options.accountId, s.machineId, s.kind, s.id])}`
  }
  async list(raw: typeof ReportListRequest._output) {
    this.active()
    const q = ReportListRequest.parse(raw)
    let response: unknown
    try {
      response = await this.options.transport.list(q)
    } catch (e) {
      // A fresh account has no reports; a relay claim alone never hides a pinned directory.
      if (q.after === undefined && isUnprovisioned(e) && this.options.unprovisioned) {
        if (await this.options.unprovisioned()) return ReportListPage.parse({ items: [] })
        throw new Error('Encrypted directory missing at relay')
      }
      throw e
    }
    this.active()
    reportBytes(response, 32_768)
    const page = ReportListPage.parse(response)
    let last = q.after ?? ''
    for (const h of page.items) {
      if (h.machineId !== q.machineId || h.kind !== q.kind || h.id <= last)
        throw new Error('wrong report list')
      last = h.id
    }
    if (page.next && (page.items.length !== 32 || page.next !== last))
      throw new Error('invalid report cursor')
    // Heads are untrusted discovery hints; read() authenticates before display.
    return page
  }
  async read(raw: ReportStream, minimum?: ReportHead): Promise<ReportProjection> {
    this.active()
    const stream = ReportStream.parse(raw)
    if (minimum && !sameStream(minimum, stream)) throw new Error('wrong report head')
    if (!minimum || this.options.memo === false) return this.readVerified(stream, minimum)
    const key = this.key(stream)
    const known = this.memo.get(key)
    if (known && known.head.revision === minimum.revision && known.head.digest === minimum.digest) {
      // Still requires an unlocked trusted vault, like the durable checkpoint path.
      await this.options.keyForEpoch(known.epoch)
      this.active()
      return known.projection
    }
    const id = `${key}@${minimum.revision}:${minimum.digest}`
    const pending = this.reading.get(id)
    if (pending) return pending
    const p = this.readVerified(stream, minimum).finally(() => {
      if (this.reading.get(id) === p) this.reading.delete(id)
    })
    this.reading.set(id, p)
    return p
  }
  private remember(key: string, c: Checkpoint) {
    if (this.options.memo === false || this.locked) return
    const known = this.memo.get(key)
    if (!known || known.head.revision <= c.head.revision) this.memo.set(key, c)
  }
  private async readVerified(stream: ReportStream, minimum?: ReportHead) {
    const key = this.key(stream)
    const before = await idb.get<Checkpoint>(key)
    let current = before
    let advertised = minimum ?? before?.head
    if (before) await this.options.keyForEpoch(before.epoch)
    this.active()
    // Advertised head equals the verified checkpoint: nothing new to authenticate.
    if (
      before &&
      minimum &&
      before.head.revision === minimum.revision &&
      before.head.digest === minimum.digest
    ) {
      this.remember(key, before)
      return before.projection
    }
    for (let pageNumber = 0; pageNumber < 4096; pageNumber++) {
      this.active()
      const response = await this.options.transport.read({
        ...stream,
        after: current?.head.revision ?? 0,
      })
      this.active()
      reportBytes(response, REPORT_PAGE_BYTES)
      const page = ReportReadPage.parse(response)
      const head = page.head
      if (
        !sameStream(head, stream) ||
        head.revision < (current?.head.revision ?? 0) ||
        (advertised && head.revision < advertised.revision)
      )
        throw new Error('report rollback or context swap')
      if (advertised && head.revision === advertised.revision && head.digest !== advertised.digest)
        throw new Error('report fork')
      advertised = head
      for (const record of page.records) {
        reportBytes(record)
        const c = record.context
        if (
          c.accountId !== this.options.accountId ||
          c.runId !== `machine:${stream.machineId}` ||
          c.objectId !== stream.id ||
          c.kind !== stream.kind ||
          c.seq !== (current?.head.revision ?? 0) + 1 ||
          c.seq > head.revision
        )
          throw new Error('wrong report context or gap')
        // Epochs never decrease along a stream; older = forged with a retired (revoked) key.
        if (current && c.epoch < current.epoch) throw new Error('report epoch downgrade')
        const key = await this.options.keyForEpoch(c.epoch)
        this.active()
        const plaintext = await openContent(key, c, record.box)
        this.active()
        if (plaintext.byteLength > REPORT_PLAINTEXT_BYTES) throw new Error('report too large')
        const body = ReportPlaintext.parse(
          JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(plaintext)),
        )
        if (
          body.value.machineId !== stream.machineId ||
          reportKind(body.value.frame) !== stream.kind ||
          body.previous !== (current?.head.digest ?? null)
        )
          throw new Error('wrong report body or ancestry')
        const source = reportSourceId(body.value.frame)
        if (current && current.source !== source) throw new Error('report source changed')
        const digest = await hashCanonicalB64('viko/entity-revision/v1', record)
        current = {
          epoch: c.epoch,
          head: { ...stream, revision: c.seq, digest },
          source,
          projection: projectReport(stream.machineId, body.value.frame, current?.projection),
        }
      }
      if (current?.head.revision === head.revision) {
        if (current.head.digest !== head.digest) throw new Error('report fork')
        const verified = current
        await this.options.keyForEpoch(verified.epoch)
        this.active()
        await idb.update<Checkpoint>(key, (existing) => {
          this.active()
          if (canonicalize(existing?.head ?? null) !== canonicalize(before?.head ?? null))
            throw new Error('report checkpoint changed; retry')
          return verified
        })
        this.active()
        this.remember(key, verified)
        return verified.projection
      }
      if (!page.records.length) throw new Error('truncated report history')
    }
    throw new Error('report history too large')
  }
}
/** Same authenticated account HTTP session as content transport. No key acquisition here. */
export function browserReportTransport(fetcher: typeof fetch, origin = ''): ReportTransport {
  async function request(method: 'list' | 'read', input: unknown, max: number) {
    reportBytes(input, 4096)
    const response = await fetcher(`${origin}/api/content/report/${method}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) throw await contentRequestError(response, 'Encrypted report request failed')
    if (!response.body) throw new Error('report request failed')
    const reader = response.body.getReader(),
      chunks: Uint8Array[] = []
    let size = 0
    try {
      for (;;) {
        const next = await reader.read()
        if (next.done) break
        size += next.value.byteLength
        if (size > max) throw new Error('report response too large')
        chunks.push(next.value)
      }
    } catch (error) {
      await reader.cancel().catch(() => {})
      throw error
    } finally {
      reader.releaseLock()
    }
    const bytes = new Uint8Array(size)
    let offset = 0
    for (const chunk of chunks) {
      bytes.set(chunk, offset)
      offset += chunk.byteLength
    }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as unknown
  }
  return {
    list: (q) => request('list', ReportListRequest.parse(q), 32_768),
    read: (q) => request('read', ReportReadRequest.parse(q), REPORT_PAGE_BYTES),
  }
}

const REPORT_PAGES = 16
/** How long one report list answer is shared by every projection that asks for it. */
export const REPORT_LIST_TTL_MS = 1_500

type ListCache = ReturnType<typeof shortCache<typeof ReportListPage._output>>

/**
 * Encrypted accounts: machine doctor/labels, landing and build cards come only from
 * authenticated report projections; hub machine rows supply ids/names/online state.
 *
 * burst-guard: one broadcast refetches machines, presence, load and usage together; they share
 * one report list per (machine, kind, cursor) for `listTtlMs` (and one hub machine list), and
 * only streams whose listed head changed since this session last verified them are read.
 * `listTtlMs: 0` is the legacy behaviour (no sharing, no in-memory checkpoints).
 *
 * Returns `changed()`: call it on each report broadcast (`work{machine|landing|build}`) so the
 * refetches it triggers list afresh (they still share that one fresh list).
 */
export function installPrivateReports(
  api: Api,
  o: {
    account: () => Promise<string | undefined>
    keyForEpoch(accountId: string, epoch: number): Promise<WebCryptoKey>
    transport: ReportTransport
    unprovisioned?(accountId: string): Promise<boolean>
    listTtlMs?: number
    meter?: Pick<RequestMeter, 'record'>
    now?: () => number
  },
) {
  const ttl = o.listTtlMs ?? REPORT_LIST_TTL_MS
  const now = o.now ?? Date.now
  const meter = o.meter ?? requestMeter
  const transport: ReportTransport = {
    list: (q) => {
      meter.record('report.list')
      return o.transport.list(q)
    },
    read: (q) => {
      meter.record('report.read')
      return o.transport.read(q)
    },
  }
  let current:
    | {
        accountId: string
        reader: BrowserReports
        list: ListCache
      }
    | undefined
  const reader = async () => {
    const accountId = await o.account()
    if (!accountId) throw new Error('Encrypted reports locked')
    if (current?.accountId !== accountId) {
      current?.reader.lock()
      const check = o.unprovisioned
      current = {
        accountId,
        reader: new BrowserReports({
          accountId,
          keyForEpoch: (epoch) => o.keyForEpoch(accountId, epoch),
          transport,
          ...(check ? { unprovisioned: () => check(accountId) } : {}),
          memo: ttl > 0,
        }),
        list: shortCache(ttl, now),
      }
    }
    return current
  }
  const hubMachineList = api.machines
  const hubCache = shortCache<Machine[]>(ttl, now)
  const hubMachines = () => hubCache('machines', () => hubMachineList())
  async function projections(machineIds: string[], kind: ReportKind) {
    const { reader: r, list: cachedList } = await reader()
    const out: ReportProjection[] = []
    for (const machineId of machineIds) {
      let after: string | undefined
      for (let page = 0; page < REPORT_PAGES; page++) {
        const q = { machineId, kind, ...(after ? { after } : {}) }
        const list = await cachedList(JSON.stringify([machineId, kind, after ?? null]), () =>
          r.list(q),
        )
        for (const head of list.items) {
          try {
            out.push(await r.read({ machineId, kind, id: head.id }, head))
          } catch (e) {
            // Fail closed per stream: an unauthentic report is never shown.
            if (e instanceof Error && e.message === 'reports locked') throw e
          }
        }
        if (!list.next) break
        after = list.next
      }
    }
    return out
  }
  const ids = async () => (await hubMachines()).map((m) => m.id)
  api.machines = async () => {
    const machines = await hubMachines()
    const status = new Map<string, Extract<ReportProjection, { kind: 'machine' }>>()
    // The daemon's own viko.project.yaml machine key, from its newest project list.
    const keys = new Map<
      string,
      { key: string; ts: number; report?: import('@viko/protocol').MachineProjectsFrame }
    >()
    for (const p of await projections(
      machines.map((m) => m.id),
      'machine',
    ))
      if (p.kind === 'machine') status.set(p.machineId, p)
      else if (p.kind === 'projects' && p.report.ts >= (keys.get(p.machineId)?.ts ?? -1))
        keys.set(p.machineId, { key: p.report.machine, ts: p.report.ts, report: p.report })
    return machines.map((m): Machine => {
      const p = status.get(m.id),
        configKey = keys.get(m.id)?.key
      // ---- account-projects: this machine's checkouts, candidates and defaults ----
      const r = keys.get(m.id)?.report
      if (r?.checkouts || r?.candidates || r?.defaults)
        m = {
          ...m,
          ...(r.checkouts ? { checkouts: r.checkouts } : {}),
          ...(r.candidates ? { candidates: r.candidates } : {}),
          ...(r.defaults ? { projectDefaults: r.defaults } : {}),
        }
      // ---- end account-projects ----
      if (!p) return configKey ? { ...m, configKey } : m
      const s = p.status
      return {
        ...m,
        ...(configKey ? { configKey } : {}),
        caps: { maxRuns: s.maxRuns, running: s.activeRuns },
        // inbox-detail: stall evidence and report time (staleness)
        ...(s.blockers ? { blockers: s.blockers } : {}),
        ...(s.pendingShips ? { pendingShips: s.pendingShips } : {}),
        ...(s.machineResources ? { machineResources: s.machineResources } : {}), // machine-resources
        reportAt: s.ts,
        ...(s.providerAccounts ? { providerAccounts: s.providerAccounts } : {}),
        ...(s.usage ? { usage: s.usage } : {}), // claude-accounts
        ...(s.reportStats ? { reportStats: s.reportStats } : {}), // burst-guard
        ...(s.doctor
          ? {
              doctor: p.doctor,
              doctorSummary: s.doctor.summary,
              doctorAt: s.doctor.at,
              capabilities: s.doctor.capabilities,
            }
          : {}),
      }
    })
  }
  // Machine-discovered projects (`viko project add`): only from authenticated reports.
  api.machineProjects = async () =>
    (await projections(await ids(), 'machine')).flatMap((p) =>
      p.kind === 'projects' ? [{ machineId: p.machineId, report: p.report }] : [],
    )
  // ---- g4-cost: recent-run usage summaries, newest report per machine ----
  api.runUsages = async (machineIds) =>
    // Machines named by the rows being listed: no separate hub machine lookup.
    (await projections(machineIds ? [...machineIds] : await ids(), 'machine')).flatMap((p) =>
      p.kind === 'usage' ? [{ machineId: p.machineId, runs: p.report.runs, ts: p.report.ts }] : [],
    )
  // ---- end g4-cost ----
  // ---- lock-follows-os: this computer's screen-lock / idle report for the app lock ----
  api.machinePresence = async (machineId) =>
    (await projections([machineId], 'machine')).flatMap((p) =>
      p.kind === 'presence' ? [p.report] : [],
    )[0]
  // ---- end lock-follows-os ----
  // G1 workstation load: only from authenticated `machine.load` revisions.
  api.machineLoad = async () =>
    (await projections(await ids(), 'machine')).flatMap((p) =>
      p.kind === 'load' ? [{ machineId: p.machineId, report: p.report }] : [],
    )
  const newest = <T extends { updatedAt: number | null }>(a: T, b: T) =>
    (b.updatedAt ?? 0) - (a.updatedAt ?? 0)
  api.landings = async (q = {}) =>
    (await projections(await ids(), 'landing'))
      .flatMap((p) => (p.kind === 'landing' ? [p.landing] : []))
      .filter(
        (l) =>
          (!q.projectId || l.projectId === q.projectId) &&
          (!q.workstreamId || l.workstreamId === q.workstreamId) &&
          (!q.state || l.state === q.state),
      )
      .sort(newest)
  api.builds = async (q = {}) =>
    (await projections(await ids(), 'build'))
      .flatMap((p) => (p.kind === 'build' ? [p.build] : []))
      .filter(
        (b) =>
          (!q.projectId || b.projectId === q.projectId) && (!q.laneId || b.laneId === q.laneId),
      )
      .sort(newest)
  // ---- g2g3-alerts: device/usage alerts ride the hygiene report stream (kind `device-alert`) ----
  api.deviceAlerts = async () =>
    (await projections(await ids(), 'hygiene')).flatMap((p) => {
      if (p.kind !== 'hygiene') return []
      const a = DeviceAlertReport.safeParse(p.report.report)
      return a.success && !a.data.resolvedAt ? [a.data] : []
    })
  // ---- end g2g3-alerts ----
  // ---- g13-digests: changelog + digest reports ride the hygiene stream (authenticated only) ----
  api.changelogs = async () =>
    (await projections(await ids(), 'hygiene')).flatMap((p) => {
      if (p.kind !== 'hygiene') return []
      const c = ChangelogReport.safeParse(p.report.report)
      return c.success ? [c.data] : []
    })
  api.digests = async () =>
    (await projections(await ids(), 'hygiene')).flatMap((p) => {
      if (p.kind !== 'hygiene') return []
      const d = DigestReport.safeParse(p.report.report)
      return d.success ? [d.data] : []
    })
  // ---- end g13-digests ----
  api.build = async (id) => {
    const build = (await api.builds()).find((b) => b.buildId === id)
    if (!build) throw new Error('Build not found')
    return build
  }
  return {
    changed() {
      current?.list.clearSettled()
      hubCache.clearSettled()
    },
  }
}
